import React, { useState } from "react";
import { Warning, Prohibit } from "@phosphor-icons/react";
import { StatRow } from "./StatRow";
import { fmtL100, monthLabel } from "./monthUtils";
import { ThresholdSetting } from "./ThresholdSetting";

const TruckDrivers = ({ drivers }) => (
  <div className="space-y-1">
    <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Chauffeurs ce mois sur ce camion</div>
    {drivers.map((d) => (
      <div key={d.name} className="flex items-center gap-2 text-xs">
        <span className={`font-bold flex-1 ${d.is_suspect ? "text-red-600" : ""}`}>{d.name}</span>
        <span className="text-zinc-500">{d.count} plein{d.count > 1 ? "s" : ""}</span>
        <span className="mono font-bold w-24 text-right">{fmtL100(d.avg_l_per_100)}</span>
      </div>
    ))}
  </div>
);

const AberrantList = ({ items }) => (
  <div className="space-y-1.5" data-testid="fuel-aberrant-list">
    <p className="text-xs text-zinc-500 mb-2">
      Ces pleins ne sont pas comptés dans les moyennes : erreur de kilométrage probable, plein partiel ou plein manquant. Vérifiez-les dans l'historique.
    </p>
    {items.length === 0 && <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 p-6 text-center">Aucun plein aberrant ce mois-ci.</div>}
    {items.map((a) => (
      <div key={a.entry_id} className="border border-zinc-300 bg-zinc-50 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid={`fuel-aberrant-${a.entry_id}`}>
        <Prohibit size={16} weight="bold" className="text-zinc-500" />
        <span className="mono font-bold uppercase">{a.license_plate}</span>
        <span className="text-xs text-zinc-500">{a.date.slice(0, 10)} · {a.driver || "—"}</span>
        <span className="text-xs flex-1 min-w-[200px]">{a.aberrant_reason}</span>
        <span className="mono text-xs text-zinc-600">{a.liters} L / {a.delta_km} km = <b>{a.l_per_100} L/100</b></span>
      </div>
    ))}
  </div>
);

export const FuelStatsMonthly = ({ data, threshold, onThresholdChange, onSaveThreshold }) => {
  const [view, setView] = useState("trucks");
  const t = data?.threshold_l_per_100 ?? threshold;
  const tabs = [
    { k: "trucks", label: "Camions", n: data?.trucks.length || 0, sus: (data?.trucks || []).filter((x) => x.is_suspect).length },
    { k: "drivers", label: "Chauffeurs", n: data?.drivers.length || 0, sus: (data?.drivers || []).filter((x) => x.is_suspect).length },
    { k: "aberrant", label: "Pleins aberrants", n: data?.aberrant.length || 0, sus: 0 },
  ];
  return (
    <div className="space-y-4" data-testid="fuel-stats-monthly">
      <ThresholdSetting threshold={threshold} onChange={onThresholdChange} onSave={onSaveThreshold} />
      <div className="flex border-b-2 border-zinc-900 flex-wrap" data-testid="stats-subtabs">
        {tabs.map(({ k, label, n, sus }) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 transition-colors ${view === k ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
            data-testid={`stats-subtab-${k}`}
          >
            {label} <span className="mono opacity-70">({n})</span>
            {sus > 0 && <span className="px-1.5 py-0.5 text-[9px] bg-red-600 text-white inline-flex items-center gap-1"><Warning size={9} weight="fill" /> {sus}</span>}
          </button>
        ))}
      </div>
      {view !== "aberrant" && (
        <div className="hidden md:grid grid-cols-[minmax(160px,1.2fr)_110px_90px_130px_110px] gap-3 px-3 text-[9px] uppercase tracking-widest font-bold text-zinc-500">
          <span>{view === "trucks" ? "Camion" : "Chauffeur"}</span>
          <span className="text-right">Moyenne {monthLabel(data?.month || "2000-01", true)}</span>
          <span className="text-right">vs {monthLabel(data?.prev_month || "2000-01", true)}</span>
          <span>6 derniers mois</span>
          <span className="text-right">Pleins</span>
        </div>
      )}
      <div className="space-y-1.5">
        {view === "trucks" && data?.trucks.map((r) => (
          <StatRow key={r.truck_id} row={r} title={r.license_plate} subtitle={`${r.brand} · ${r.site}`} threshold={t}
            testId={`fuel-stat-truck-${r.license_plate}`} details={r.drivers.length ? <TruckDrivers drivers={r.drivers} /> : null} />
        ))}
        {view === "drivers" && data?.drivers.map((r) => (
          <StatRow key={r.name} row={r} title={r.name} subtitle="" threshold={t} testId={`fuel-stat-driver-${r.name}`} />
        ))}
        {view === "aberrant" && <AberrantList items={data?.aberrant || []} />}
        {view !== "aberrant" && data && (view === "trucks" ? data.trucks : data.drivers).length === 0 && (
          <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 p-8 text-center" data-testid="fuel-stats-empty">
            Pas encore assez de pleins ce mois-ci pour calculer une consommation.
          </div>
        )}
      </div>
    </div>
  );
};
